import { AutoCompleteSelect } from 'aa_common/front-end/antd'
import { RedirectToMasterPageLink, UnderlineText, UploadAttachment, UploadImage } from 'aa_common/front-end/components'
import FormLinkListField from 'aa_common/front-end/components/form/form-link-list-field'
import { defaultTo } from 'aa_common/front-end/helpers'
import { Col, Row, Typography } from 'antd'
import { CAUSE_TYPES, ENDPOINTS, FEATURES_ID } from 'common/constants'
import { handlePermissionByFeatureId, yen } from 'common/helpers'
import { findAccountingLedger } from 'common/helpers/ledger-helper'
import { useNextEffect } from 'common/hooks/use-next-effect'
import { getNs } from 'common/i18n-config'
import Form from 'components/molecules/Form'
import { get, isEqual } from 'lodash'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { selectUserPermission } from 'store/session/selectors'
import { fetchCauseList } from 'store/settings/cause/actions'
import { selectCauseList } from 'store/settings/cause/selectors'
import { selectorLedgerSettingsList } from 'store/settings/ledgerSetting/selectors'

import { defaultBookInfoValue, useImpairmentBookInfo, useImpairmentLossAmount } from '../hooks/hooks'
import { useRemainBookValue } from '../hooks/use-remain-book-value'
import { FIELD_NAMES } from '../validation'
import { ImpairmentLost } from './impairment-lost'
import { ImpairmentMethod } from './impairment-method'
import { ImpairmentResidual } from './impairment-residual'
import ImpairmentDate from './ImpairmentDate'
import { ResidualServiceLife } from './residual-service-life'
import { ResidualUsagePeriod } from './residual-usage-period'

const { t, useNsReady } = getNs(['components/organisms/assets/asset-impairment', 'common'])

const { FormField, Group } = Form

export const FormContent = () => {
  useNsReady()

  const { control, errors, setValue, trigger } = useFormContext()
  const dispatch = useDispatch()
  const { data: causes } = useSelector(selectCauseList, isEqual)
  const { data: ledgerSettings } = useSelector(selectorLedgerSettingsList, isEqual)
  const { data: permissions } = useSelector(selectUserPermission, isEqual)

  const accountingLedger = useMemo(() => findAccountingLedger(ledgerSettings), [ledgerSettings])
  const impairmentReasons = useMemo(
    () => defaultTo(causes, []).filter(cause => cause.type === CAUSE_TYPES.IMPAIRMENT),
    [causes]
  )
  const [bookInformation, loading] = useImpairmentBookInfo(FIELD_NAMES.impaired_at, FIELD_NAMES.impairment_method)
  const { book_value: impairmentBookValue, date: impairReferenceDate } = defaultTo(
    bookInformation,
    defaultBookInfoValue
  )
  const impairmentLossAmount = useImpairmentLossAmount(impairmentBookValue)
  const remainingBookValue = useRemainBookValue(impairmentBookValue)

  useNextEffect(() => {
    setValue(FIELD_NAMES.remaining_book_value, remainingBookValue)
    trigger(FIELD_NAMES.impair_residual_value)
  }, [setValue, remainingBookValue, trigger])

  return (
    <Group className="form-wrapper" borderLess>
      <FormField title={t('impairment_date')} required>
        <ImpairmentDate required name={FIELD_NAMES.impaired_at} error={get(errors, 'impaired_at.message')} />
      </FormField>
      <FormField title={t('impairment_reason')} required>
        <AutoCompleteSelect
          required
          name="event_cause_id"
          valueField="id"
          textField="content"
          placeholder={t('placeholder.please_select')}
          items={impairmentReasons}
          control={control}
          error={get(errors, 'event_cause_id.message')}
          customDropdownTemplate={
            <RedirectToMasterPageLink
              permissions={handlePermissionByFeatureId(FEATURES_ID.MASTER_CAUSES, permissions)}
              urlName={ENDPOINTS.CAUSES}
              pageName={t('master.causes')}
              onHandleReload={() => dispatch(fetchCauseList())}
            />
          }
        />
      </FormField>
      <FormField title={t('book_value_date')}>
        <UnderlineText loading={loading} contentAlign="right">
          <div style={{ textAlign: 'left' }}>{impairReferenceDate}&nbsp;</div>
        </UnderlineText>
      </FormField>
      <FormField title={t('impairment_book_value')}>
        <UnderlineText loading={loading} contentAlign="right">
          <Row className="impairment-book-value" wrap={false}>
            <Col className="ledger-name" flex="auto">
              <Typography.Text ellipsis>{accountingLedger?.name}</Typography.Text>
            </Col>
            <Col flex="none">
              <span className="value">{yen(impairmentBookValue)}</span>
            </Col>
          </Row>
        </UnderlineText>
      </FormField>
      <FormField title={t('impairment_loss_amount')} required>
        <ImpairmentLost impairmentAmountMaximum={impairmentLossAmount} />
      </FormField>
      <FormField title={t('accumulated_impairment_loss')}>
        <UnderlineText loading={loading} contentAlign="right">
          {yen(impairmentLossAmount)}
        </UnderlineText>
      </FormField>
      <FormField title={t('book_value_after_impairment')}>
        <UnderlineText loading={loading} contentAlign="right">
          {yen(remainingBookValue)}
        </UnderlineText>
      </FormField>
      <ResidualServiceLife ledgerSetting={accountingLedger} />
      <ResidualUsagePeriod ledgerSetting={accountingLedger} />
      <FormField title={t('depreciation_rate')}>
        <UnderlineText>0% - under construction </UnderlineText>
      </FormField>
      <FormField title={t('impair_residual_value')} required>
        <ImpairmentResidual />
      </FormField>
      <ImpairmentMethod ledgerSetting={accountingLedger} />
      <FormField className="align-start" title={t('photos')}>
        <UploadImage name="event_photos" control={control} />
      </FormField>
      <FormField className="align-start" title={t('attachments')}>
        <UploadAttachment name="event_attachments" />
      </FormField>
      <FormField className="align-start" title={t('links')}>
        <FormLinkListField name="event_urls" control={control} placeholder={t('placeholder.links')} />
      </FormField>
    </Group>
  )
}
